import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@clerk/react";
import { CONTENT } from "@/content";
import { publicGet } from "@/lib/api";

const L = CONTENT.library;

const Card = ({ item, i }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: i * 0.05 }}>
    <Link to={`/library/${item.slug}`} className="lib-card" data-testid={`library-card-${item.slug}`}>
      <div className="row-tags">
        {item.category && <span className="pill">{item.category}</span>}
        {item.embed_src && <span className="pill light" data-testid={`library-card-video-${item.slug}`}>{L.videoPill}</span>}
      </div>
      <h2 className="lib-card-title">{item.title}</h2>
      <p className="lib-card-verse">{item.verse_text}</p>
      <p className="verse-ref">{item.verse_reference}</p>
    </Link>
  </motion.div>
);

const Chips = ({ options, value, onChange, testId }) => (
  <div className="chips" data-testid={testId}>
    {[["", L.allLabel], ...options.map((o) => [o, o])].map(([v, label]) => (
      <button key={label} className={`tab ${value === v ? "active" : ""}`} onClick={() => onChange(v)} data-testid={`${testId}-${v || "all"}`}>{label}</button>
    ))}
  </div>
);

const TrackBanner = () => {
  const { isSignedIn } = useAuth();
  const t = L.track[isSignedIn ? "signedIn" : "signedOut"];
  return (
    <motion.section className="nb-card track-banner" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.2 }} data-testid="library-track-banner">
      <span className="track-ripple" aria-hidden="true"><span className="mini-ripple" /><span className="mini-ripple" style={{ animationDelay: "1.2s" }} /><span className="journey-dot" /></span>
      <div className="track-text">
        <span className="pill">{t.label}</span>
        <h2 className="journey-title">{t.title}</h2>
        <p className="journey-text">{t.text}</p>
      </div>
      <Link to={t.primary.to} className="nb-btn" data-testid="library-track-button">{t.primary.label}</Link>
    </motion.section>
  );
};

export default function Library() {
  const [params] = useSearchParams();
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [tag, setTag] = useState(params.get("tag") || "");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);

  useEffect(() => { const t = setTimeout(() => { setQuery(q); setPage(1); }, 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { publicGet("/content", { page, q: query, category, tag }).then(setData).catch(() => setData({ items: [], total: 0, categories: [], tags: [] })); }, [page, query, category, tag]);

  const pick = (setter) => (v) => { setter(v); setPage(1); };
  const pages = data ? Math.max(1, Math.ceil(data.total / 9)) : 1;
  const filtered = query || category || tag;

  return (
    <main className="page library" data-testid="library-page">
      <header className="journey-head">
        <span className="pill">{L.label}</span>
        <h1 className="heart-title">{L.title}</h1>
        <p className="heart-lead">{L.supporting}</p>
      </header>
      <TrackBanner />
      <input className="nb-input lib-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={L.searchPlaceholder} data-testid="library-search-input" />
      {data && data.categories.length > 0 && <Chips options={data.categories} value={category} onChange={pick(setCategory)} testId="library-category" />}
      {data && data.tags.length > 0 && (
        <div className="lib-tags"><span className="field-label">{L.situationsLabel}</span><span className="field-helper">{L.situationsHint}</span><Chips options={data.tags} value={tag} onChange={pick(setTag)} testId="library-tag" /></div>
      )}
      {data && !data.items.length && <div className="nb-card" data-testid="library-empty"><p className="panel-line">{filtered ? L.noResults : L.empty}</p></div>}
      <div className="lib-grid" data-testid="library-grid">
        {data?.items.map((item, i) => <Card key={item.id} item={item} i={i} />)}
      </div>
      {pages > 1 && (
        <div className="pager">
          <button className="nb-btn ghost small" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="library-prev">{L.prev}</button>
          <span className="field-label">{page} / {pages}</span>
          <button className="nb-btn ghost small" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="library-next">{L.next}</button>
        </div>
      )}
    </main>
  );
}
